/**
 * CVO Complaints View
 * Dedicated route component for CVO complaints management
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ComplaintsView } from './index';
import { EditComplaintDialog } from '../../components/dialogs';
import Toast from '../../components/Toast';
import {
  fetchCVOComplaints,
  fetchCVOComplaint,
  selectComplaints,
  selectCurrentComplaint,
  selectComplaintLoading,
  selectComplaintError,
} from '../../store/slices/complaintSlice';
import {
  selectComplaintTypes,
  selectComplaintStatuses,
  selectDepartments,
  selectComplaintPriorities,
} from '../../store/slices/settingsSlice';
import {
  fetchOfficers,
  selectOfficers,
  selectOfficerLoading,
} from '../../store/slices/officerSlice';
import complaintService from '../../services/complaintService';
import noteService from '../../services/noteService';

const CVOComplaintsView = () => {
  const dispatch = useDispatch();
  
  // Redux state selectors - complaints
  const complaints = useSelector(selectComplaints);
  const currentComplaint = useSelector(selectCurrentComplaint);
  const complaintsLoading = useSelector(selectComplaintLoading);
  const complaintsError = useSelector(selectComplaintError);
  
  // Redux state selectors - settings
  const complaintTypes = useSelector(selectComplaintTypes);
  const complaintStatuses = useSelector(selectComplaintStatuses);
  const departments = useSelector(selectDepartments);
  const priorities = useSelector(selectComplaintPriorities);
  
  // Redux state selectors - officers
  const officersFromRedux = useSelector(selectOfficers);
  const officersLoading = useSelector(selectOfficerLoading);
  
  // Transform officers data for dialogs
  const transformOfficerData = (officer) => {
    let status = 'Inactive';
    if (officer.status === 'Active' || officer.status === 'active' || officer.status === 1) {
      status = 'Active';
    } else if (officer.status === 'Delete' || officer.status === 'delete') {
      status = 'Delete';
    } else if (officer.status === 'Inactive' || officer.status === 'inactive' || officer.status === 0) {
      status = 'Inactive';
    }
    
    return {
      id: officer.id,
      name: officer.name || '',
      designation: officer.designation_name || officer.designation || '',
      status: status,
    };
  };

  const officers = Array.isArray(officersFromRedux) 
    ? officersFromRedux.map(transformOfficerData)
    : [];
  
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaintLogs, setComplaintLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isLoadingComplaintDetails, setIsLoadingComplaintDetails] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingComplaint, setIsUpdatingComplaint] = useState(false);
  const [isSendingNote, setIsSendingNote] = useState(false);
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success',
  });

  // Fetch complaints and officers on component mount
  useEffect(() => {
    console.log('CVOComplaintsView: Fetching CVO complaints and officers...');
    dispatch(fetchCVOComplaints());
    dispatch(fetchOfficers({ params: {}, role: 'cvo' }));
  }, [dispatch]);

  // Log data for debugging
  useEffect(() => {
    console.log('CVOComplaintsView: Complaints from Redux:', complaints);
    console.log('CVOComplaintsView: Loading state:', complaintsLoading);
    console.log('CVOComplaintsView: Error state:', complaintsError);
    console.log('CVOComplaintsView: Settings data:', { complaintTypes, complaintStatuses, departments, priorities });
    console.log('CVOComplaintsView: Officers data:', officers);
  }, [complaints, complaintsLoading, complaintsError, complaintTypes, complaintStatuses, departments, priorities, officers]);

  const resetEditState = () => {
    setSelectedComplaint(null);
    setComplaintLogs([]);
    setIsLoadingLogs(false);
  };

  // Handler functions
  const handleComplaintEdit = async (complaint) => {
    try {
      setIsLoadingComplaintDetails(true);
      
      // Fetch full complaint details from API
      const result = await dispatch(fetchCVOComplaint(complaint.id));
      
      if (result.type === 'complaints/fetchCVOOne/fulfilled' && result.payload) {
        const apiData = result.payload;
        
        console.log('API Data files:', apiData.files);
        
        // Separate files by type
        const supportingDocuments = apiData.files 
          ? apiData.files.filter(file => file.file_type === 'supporting')
          : [];
        const generalDocuments = apiData.files 
          ? apiData.files.filter(file => file.file_type === 'general')
          : [];
        
        console.log('Supporting docs:', supportingDocuments);
        console.log('General docs:', generalDocuments);
        
        // Transform API response to match dialog format
        const transformedComplaint = {
          id: complaint.id, // Use the DB id
          complaint_id: apiData.complaint_id,
          name: apiData.user_details?.name || complaint.name || '',
          complainant: apiData.user_details?.name || complaint.name || '',
          mobile: apiData.user_details?.mobile || complaint.mobile || '',
          email: apiData.user_details?.email || complaint.email || '',
          address: apiData.address || complaint.address || '',
          complaint: apiData.your_complaint || complaint.description || complaint.complaint || '',
          description: apiData.your_complaint || complaint.description || complaint.complaint || '',
          type: apiData.complaint_type || complaint.type || '',
          status: apiData.complaint_status || complaint.status || 'Pending',
          department: complaint.department || '',
          dueDays: complaint.dueDays || '',
          assigned_day_by_cvo: apiData.assigned_day_by_cvo || complaint.assigned_day_by_cvo || '',
          priority: complaint.priority || 'Medium',
          assignedOfficer: complaint.assignedOfficer || '',
          assigned_to: apiData.assigned_to || complaint.assigned_to || null,
          assignedOfficerId: apiData.assigned_to || complaint.assigned_to || null,
          created_at: apiData.created_at || complaint.created_at || complaint.date || '',
          updated_at: apiData.updated_at || complaint.updated_at || '',
          user_id: apiData.user_id || apiData.user_details?.id || complaint.user_id || complaint.user?.id,
          user: apiData.user_details || complaint.user || null,
          supporting_documents: supportingDocuments,
          general_documents: generalDocuments,
          documents: apiData.files && apiData.files.length > 0 
            ? apiData.files 
            : (complaint.documents || [])
        };
        
        setSelectedComplaint(transformedComplaint);
        // Fetch complaint logs after details load
        setComplaintLogs([]);
        setIsLoadingLogs(true);
        try {
          const logsResult = await complaintService.getComplaintLogs(complaint.id);
          if (logsResult.success && Array.isArray(logsResult.data?.data)) {
            setComplaintLogs(logsResult.data.data);
          } else {
            setComplaintLogs([]);
          }
        } catch (logsError) {
          console.error('Error fetching complaint logs:', logsError);
          setComplaintLogs([]);
        } finally {
          setIsLoadingLogs(false);
        }
      } else {
        // If API call fails, use the existing complaint data
        setSelectedComplaint(complaint);
        setComplaintLogs([]);
        setIsLoadingLogs(false);
      }
    } catch (error) {
      console.error('Error fetching complaint details:', error);
      // Fallback to using the existing complaint data
      setSelectedComplaint(complaint);
      setComplaintLogs([]);
      setIsLoadingLogs(false);
    } finally {
      setIsLoadingComplaintDetails(false);
    }
  };

  const handleSaveComplaint = async (formData) => {
    if (!formData || !selectedComplaint) {
      setSelectedComplaint(null);
      return;
    }

    try {
      setIsUpdatingComplaint(true);
      // Call the API to update complaint data (not status)
      const result = await complaintService.updateCVOComplaintData(selectedComplaint.id, formData);
      
      if (result.success) {
        // Refresh complaints list to get updated data
        await dispatch(fetchCVOComplaints());
        // Close the dialog
        resetEditState();
      } else {
        console.error('Failed to update complaint:', result.message);
        // You might want to show a toast/notification here
      }
    } catch (error) {
      console.error('Error updating complaint:', error);
      // You might want to show a toast/notification here
    } finally {
      setIsUpdatingComplaint(false);
      // Close dialog even on error
      resetEditState();
    }
  };

  const handleStatusChange = async (complaintId, newStatus) => {
    try {
      setIsUpdatingStatus(true);
      const result = await complaintService.updateCVOComplaintStatus(complaintId, {
        status: newStatus
      });
      
      if (result.success) {
        // Refresh complaints list to get updated status
        await dispatch(fetchCVOComplaints());
        // Close the dialog
        resetEditState();
      } else {
        console.error('Failed to update status:', result.message);
        // You might want to show a toast/notification here
      }
    } catch (error) {
      console.error('Error updating complaint status:', error);
      // You might want to show a toast/notification here
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAddNote = async (attachments = [], selectedRecipients = []) => {
    if (!newNote.trim() && attachments.length === 0) return;
    if (!selectedComplaint || !selectedComplaint.id) return;

    setIsSendingNote(true);
    try {
      // Use complaint_id (string like "TYO966075714") for the API
      const complaintId = selectedComplaint.complaint_id || selectedComplaint.id;
      
      // Process recipients: extract IDs from selectedRecipients
      const sendTo = [];
      if (Array.isArray(selectedRecipients) && selectedRecipients.length > 0) {
        selectedRecipients.forEach((recipient) => {
          if (typeof recipient === 'string') {
            // Check if it's an officer (format: "officer-{id}")
            if (recipient.startsWith('officer-')) {
              const officerId = recipient.replace('officer-', '');
              if (officerId) {
                sendTo.push(officerId);
              }
            } 
            // Check if it's the complainant
            else if (recipient === 'complainant') {
              // Get complainer ID from complaint data
              const complainerId = selectedComplaint.user?.id || selectedComplaint.user_id;
              if (complainerId) {
                sendTo.push(complainerId);
              }
            }
          } else if (typeof recipient === 'object' && recipient.id) {
            // Handle object format
            const recipientId = recipient.id;
            if (recipientId.startsWith('officer-')) {
              const officerId = recipientId.replace('officer-', '');
              if (officerId) {
                sendTo.push(officerId);
              }
            } else if (recipientId === 'complainant') {
              const complainerId = selectedComplaint.user?.id || selectedComplaint.user_id;
              if (complainerId) {
                sendTo.push(complainerId);
              }
            }
          }
        });
      }

      const noteData = {
        message: newNote.trim(),
        sendTo: sendTo,
        files: attachments || [],
      };

      const result = await noteService.sendInternalNote(complaintId, noteData);
      
      if (result.success) {
        // Refresh complaint logs to show new note
        if (selectedComplaint.id) {
          try {
            const logsResponse = await complaintService.getComplaintLogs(selectedComplaint.id);
            if (logsResponse.success) {
              setComplaintLogs(Array.isArray(logsResponse.data) ? logsResponse.data : []);
            }
          } catch (error) {
            console.error('Error refreshing complaint logs:', error);
          }
        }
        setNewNote('');
        setToast({
          isVisible: true,
          message: result.message || result.data?.message || 'Internal note sent successfully',
          type: 'success',
        });
      } else {
        console.error('Failed to add note:', result.message);
        setToast({
          isVisible: true,
          message: result.message || result.data?.message || 'Failed to send internal note',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error adding note:', error);
      setToast({
        isVisible: true,
        message: error.message || 'An error occurred while sending the note',
        type: 'error',
      });
    } finally {
      setIsSendingNote(false);
    }
  };

  return (
    <>
      <ComplaintsView
        complaints={complaints}
        loading={complaintsLoading}
        error={complaintsError}
        onComplaintEdit={handleComplaintEdit}
      />

      {/* Complaint Edit Modal */}
      <EditComplaintDialog
        isOpen={!!selectedComplaint}
        complaint={selectedComplaint}
        complaintStatuses={complaintStatuses}
        complaintTypes={complaintTypes}
        departments={departments}
        officers={officers}
        priorities={priorities}
        complaintLogs={complaintLogs}
        logsLoading={isLoadingLogs}
        newNote={newNote}
        onNoteChange={setNewNote}
        onAddNote={handleAddNote}
        onSave={handleSaveComplaint}
        onStatusChange={handleStatusChange}
        onClose={resetEditState}
        showHistory={true}
        loading={isLoadingComplaintDetails || isUpdatingStatus || isUpdatingComplaint || isSendingNote}
      />

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ isVisible: false, message: '', type: 'success' })}
      />
    </>
  );
};

export default CVOComplaintsView;
